<script setup>
import { useDialogPluginComponent } from 'quasar';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import HrBasicDialog from '../HrBasicDialog.vue';
import HrBtn from '../HrBtn.vue';
import HrOptionGroup from '../HrOptionGroup.vue';

/**
 * Selection dialog presented via radioDialog() or checkboxDialog() with configurable options.
 *
 * @status internal
 * @group cards-dialogs
 */
defineOptions({ name: 'HrOptionsDialog' });

const props = defineProps({
  /** Dialog headline. */
  title: {
    type: String,
    required: true,
  },
  /** Supporting text providing context for the selection. Omit to show only the title. */
  message: {
    type: String,
    default: null,
  },
  /**
   * Initial selection. Pass a single value for radio, an array of values for checkbox.
   * Defaults to null (radio) or [] (checkbox) when not provided.
   */
  model: {
    type: null,
    default: null,
  },
  /**
   * Available options. Each item must have `value` and `label`.
   * An optional `disable` field disables that individual option.
   */
  items: {
    type: Array,
    required: true,
  },
  /**
   * Selection input type rendered for the options.
   * @values radio, checkbox
   */
  type: {
    type: String,
    default: 'radio',
    validator: (v) => ['radio', 'checkbox'].includes(v),
  },
  /**
   * Returns true when the current selection is acceptable.
   * The confirm button is disabled while this returns false.
   */
  isValid: {
    type: Function,
    default: null,
  },
  /** Label for the confirm action button. Defaults to the common.buttonConfirm translation. */
  confirmLabel: {
    type: String,
    default: null,
  },
  /** Label for the cancel action button. Defaults to the common.buttonCancel translation. */
  cancelLabel: {
    type: String,
    default: null,
  },
  /**
   * Prevents dismissal via backdrop click or Escape key.
   * Defaults to true — an options dialog requires an explicit choice.
   */
  persistent: {
    type: Boolean,
    default: true,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const { t } = useI18n();
const effectiveConfirmLabel = computed(() => props.confirmLabel ?? t('common.buttonConfirm'));
const effectiveCancelLabel = computed(() => props.cancelLabel ?? t('common.buttonCancel'));

const selected = ref(
  props.type === 'checkbox' && props.model === null ? [] : props.model,
);
const isConfirmDisabled = computed(() => props.isValid !== null && !props.isValid(selected.value));
</script>

<template>
  <hr-basic-dialog
    ref="dialogRef"
    :persistent="persistent"
    @hide="onDialogHide"
  >
    <div class="hr-dialog">
      <p class="hr-dialog__headline hr-headline-small">
        {{ title }}
      </p>
      <p
        v-if="message !== null"
        class="hr-dialog__supporting-text hr-body-medium"
      >
        {{ message }}
      </p>
      <hr-option-group
        v-model="selected"
        class="hr-dialog__options"
        :options="items"
        :type="type"
      />
      <div class="hr-dialog__actions">
        <!-- @todo When the legacy theme is removed: remove :legacy-props -->
        <hr-btn
          variant="text"
          :label="effectiveCancelLabel"
          :legacy-props="{ flat: true, color: 'secondary' }"
          @click="() => onDialogCancel()"
        />
        <hr-btn
          variant="text"
          :label="effectiveConfirmLabel"
          :disable="isConfirmDisabled"
          :legacy-props="{ flat: true, color: 'primary' }"
          data-autofocus
          @click="() => onDialogOK(selected)"
        />
      </div>
    </div>
  </hr-basic-dialog>
</template>

<style lang="scss" scoped>
.hr-dialog {
  background: var(--app-surface-container-high);
  border-radius: var(--app-shape-corner-extra-large);
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.hr-dialog__headline {
  color: var(--app-on-surface);
  margin: 0 0 16px;
}

.hr-dialog__supporting-text {
  color: var(--app-on-surface-variant);
  margin: 0 0 16px;
  /* Supporting text is plain text; honor authored line breaks (\n) without allowing HTML. */
  white-space: pre-line;
}

.hr-dialog__options {
  margin-top: 0;
}

.hr-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}
</style>
